import {
    CustomProperty,
    Instalment,
    InstalmentsInfo,
    OnlineClaimRequest,
    Party,
    PaymentCard,
    Policy,
    ProductsEnum,
    ValuableItem,
    ALLIANZ,
    INSTALMENT_STATUS_IN_FLIGHT,
    INSTALMENT_STATUS_PENDING_PAYMENT,
    INTERACTION_PROCESS_CLAIM_IMPACT,
    INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE,
    INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY,
    INTERACTION_PROCESS_MTC,
    INTERACTION_PROCESS_MTC_FORCED,
    INTERACTION_PROCESS_MTC_LV,
    INTERACTION_PROCESS_NB,
    INTERACTION_PROCESS_RENEWAL,
    INTERACTION_PROCESS_SUPPORT_PORTAL_FORCE_MTC,
    INTERACTION_PROCESS_SUPPORT_PORTAL_MTC,
    MAIN_PAYMENT_CARD,
    POLICY_STATUS_MTC,
    ALLIANZ_ESSENTIALS,
    ALLIANZ_SILVER
} from "@falcon/services";
import * as moment from "moment";
import {Moment} from "moment";

export function getAddressLines(address: any): string[] {
    const addressComponents: string[] = address.fullAddress.split(", ");
    const convertedComponents: string[] = [];

    for (const component of addressComponents.slice(0, -1)) {
        const addressLine: string[] = [];

        for (const separateWord of component.split(' ')) {
            addressLine.push(separateWord.charAt(0).toUpperCase() + separateWord.slice(1).toLowerCase());
        }
        convertedComponents.push(addressLine.join(" "));
    }

    // Postcode line should have all letter in uppercase, so it is not being converted
    convertedComponents.push(addressComponents[addressComponents.length - 1]);

    return convertedComponents;
}

export function capitalize(text: string): string {
    return text.toLowerCase().split(' ').map(s => s.charAt(0).toUpperCase() + s.substring(1).toLowerCase()).join(' ');
}

//
// Retrieves policy interaction with provided interactionId
//
export function getPolicyInteraction(interactionId: string | null, policies: any): any {
    if (policies && policies.length > 0) {
        for (const policy of policies) {
            if (policy.interaction._id === interactionId) {
                return policy.interaction;
            }
        }
    }
    return "";
}

//
// Retrieves payment card type name
//
export function renderCardTypeName(card: PaymentCard): string {
    if (card.cardType.includes("VISA")) {
        return "Visa";
    }
    if (card.cardType.includes("MASTERCARD")) {
        return "Mastercard";
    }
    return "";
}

export function findMainPaymentCard(party: Party): PaymentCard | null {
    for (const card of party.paymentCards) {
        if (isMainPaymentCard(card, party)) {
            return card;
        }
    }
    return null;
}

export function findCardToReuse(party: Party, reuseCard?: boolean, mainCardId?: number): PaymentCard | undefined {
    const paymentCards: PaymentCard[] = party.paymentCards;

    if (reuseCard) {
        if (paymentCards.length === 1) {
            return paymentCards[0];
        }

        return paymentCards.find(c => c.id === mainCardId)!;
    } else {
        return undefined;
    }
}

export function findMainAnnualPaymentCardByMainCardId(party: Party, mainCardId: string): PaymentCard | null {
    for (const card of party.paymentCards) {
        if (Number(mainCardId) === card.id) {
            return card;
        }
    }
    return null;
}

export function isMainPaymentCard(card: PaymentCard, party: Party): boolean {
    const properties: CustomProperty[] = party.customProperties!;

    for (const property of properties) {
        if (property.key === MAIN_PAYMENT_CARD && Number(property.value) === card.id) {
            return true;
        }
    }
    return false;
}

export function trimFileExtension(filename: string): string {
    return filename.substring(0, filename.lastIndexOf('.'));
}

export function calculateValuablesTotalValue(valuableItems: ValuableItem[]): number {
    return valuableItems.map((item: ValuableItem) => item.value).reduce((total, next) => total + next, 0);
}

export function getInputClassName(inputValid?: boolean): string {
    if (inputValid == null) {
        return "form-control";
    } else if (inputValid) {
        return "form-control is-valid";
    } else {
        return "form-control is-invalid";
    }
}

export function setSessionStorage(key: string, value: any): void {
    window.sessionStorage.setItem(key, JSON.stringify(value));
}

export function setLocalStorage(key: string, value: any): void {
    window.localStorage.setItem(key, JSON.stringify(value));
}

export function getSessionStorage(key: string): any {
    const object: string | null = window.sessionStorage.getItem(key);
    if (object) {
        return JSON.parse(object);
    } else {
        return null;
    }
}

export function getLocalStorage(key: string): any {
    const object: string | null = window.localStorage.getItem(key);
    if (object) {
        return JSON.parse(object);
    } else {
        return null;
    }
}

export function deleteSessionStorage(key: string): void {
    window.sessionStorage.removeItem(key);
}

export function mergePolicyAndInteractionValuableItems(policies: Policy[]): ValuableItem[] {
    return policies
        .map((policy: Policy) => policy.interaction.valuableItems || [])
        .reduce((total, current) => total.concat(current), [])
        .map((valuableItem: ValuableItem) => {
            policies.forEach((policy: Policy) => {
                const items: any = policy.insuredValuableItems.filter((item: any) => item.valuableItemId === valuableItem.valuableItemId);
                if (items.length > 0) {
                    valuableItem.id = items[0].id;
                    valuableItem.description = items[0].description;
                    valuableItem.serialNumber = items[0].serialNumber;
                    valuableItem.coverLocation = items[0].coverLocation;
                }
            });
            return valuableItem;
        }).filter((valuableItem: ValuableItem) => !valuableItem.removed);
}

export function getAmountWithDecimalDigits(amount: any): string {
    const fixedAmount: number = 1 * amount;
    return new Intl.NumberFormat('en-UK', {style: 'currency', currency: 'GBP'}).format(fixedAmount);
}

export function getAmountToDisplay(amount: any): string {
    if ((amount % 1) === 0) {
        return "£" + new Intl.NumberFormat('en-UK').format(amount);
    } else {
        return getAmountWithDecimalDigits(amount);
    }
}

export function getAddress(address: any): string {
    const addressLine1: string = address.addressLine1 ? address.addressLine1 + ", " : "";
    const addressLine2: string = address.addressLine2 ? address.addressLine2 + ", " : "";
    const addressLine3: string = address.addressLine3 ? address.addressLine3 + ", " : "";

    return capitalize(addressLine1 + addressLine2 + addressLine3)
        .split(",")
        .map(s => s.trim())
        .filter(Boolean)
        .join(", ");
}

export function getModifiedMobileNumber(mobileNumber: string): string {
    const stringArray: string [] = mobileNumber.split("");
    const output: string [] = stringArray.map((letter: string, index: number) => {
        let tempString: string = "";
        tempString += letter;
        if (index === 3 || index === 6) {
            tempString += " ";
        }
        return tempString;
    });
    return output.join("");
}

export function getMotorPoliciesMinDate(policies: Policy[], productName: string, status: string): string {
    const motorNewPoliciesStartDates: any[] = [];
    let policyStartDate: string = "";
    policies.forEach((policy: Policy) => {
        policy.products.forEach((product: string) => {
            if (policy.status === status && product === productName) {
                motorNewPoliciesStartDates.push(new Date(policy.startDate));
            }
        });
    });

    if (motorNewPoliciesStartDates.length > 0) {
        const earliest = new Date(Math.min.apply(null, motorNewPoliciesStartDates));
        policyStartDate = moment(earliest.toString()).format("YYYY-MM-DD");
    }

    return policyStartDate;
}

export function downloadDocument(content: ArrayBuffer, docName: string): void {
    const mediaType = "application/pdf";
    const link = document.createElement("a");
    const blob = new Blob([content], {type: mediaType});
    const blobUrl = URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.download = docName;
    link.href = blobUrl;
    link.click();
    document.body.removeChild(link);
}

export function fromBase64ToBinary(base64: string) {
    const input = base64.replace(/\s/g, '');
    const byteCharacters = atob(input);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    return new Uint8Array(byteNumbers);
}

export function getMtcEndDate(policies: Policy[], productName: string): string {
    let policyEndDate: string = "";
    policies.forEach((policy: Policy) => {
        policy.products.forEach((product: string) => {
            if (policy.status === POLICY_STATUS_MTC && product === productName) {
                policyEndDate = moment.utc(policy.endDate, moment.ISO_8601).format("YYYY-MM-DD");
            }
        });
    });

    return policyEndDate;
}

export function formatFileSize(byteSize: any): any {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (byteSize === 0) {
        return '';
    }
    const kilobyteSize: number = parseInt(String(Math.floor(Math.log(byteSize) / Math.log(1024))), 10);
    return Math.round(byteSize / Math.pow(1024, kilobyteSize)) + ' ' + sizes[kilobyteSize];
}

export function getClaimLossDateErrorMessage(onlineClaimRequest: OnlineClaimRequest, policy: Policy): string {
    const now: Moment = moment();
    const accidentDate: Moment = moment(onlineClaimRequest.accidentDate);
    const minDateUkTimezone: string = new Date(policy.startDate).toLocaleString("en-GB", {timeZone: "Europe/London"});
    const policyMinDate: Moment = moment(minDateUkTimezone, "DD/MM/YYYY, HH:mm:ss");
    const accidentHourNumber: number = parseInt(onlineClaimRequest.accidentHour, 10);
    const accidentHour: number = accidentHourNumber === 12 ? 0 : accidentHourNumber;
    let lossDateErrorMessage: string = "";

    if (onlineClaimRequest.accidentDate && onlineClaimRequest.accidentHour && onlineClaimRequest.accidentHourTime) {
        const accident24HourFormat: number = onlineClaimRequest.accidentHourTime === "AM" ? accidentHour : accidentHour + 12;

        if (accidentDate.isAfter(now, 'day') || (accidentDate.isSame(now, 'day') && accident24HourFormat > now.hour())) {
            lossDateErrorMessage = "You cannot make claim in the future, if you are unsure please call us on 0800 030 4898";
        } else if (accidentDate.isBefore(policyMinDate, 'date') || (accidentDate.isSame(policyMinDate, 'date') && accident24HourFormat < moment(policyMinDate).hour())) {
            lossDateErrorMessage = "You cannot make claim before your cover started, if you are unsure please call us on 0800 030 4898";
        }
    }

    return lossDateErrorMessage;
}

export function getLink(redirectTo: string, disabled: boolean): string {
    return disabled ? "#" : redirectTo;
}

export function getCursorClassName(disabled: boolean): string {
    return disabled ? "cursor-pointer-default" : "cursor-pointer";
}

export const formatDateTime = (inputDate: string): string | null => {
    return formatDate(inputDate, "D MMMM YYYY, HH:mm")
}

export const formatDateDMY = (inputDate: string): string | null => {
    return formatDate(inputDate, "D MMMM YYYY")
}

export const formatDate = (inputDate: string, format: string) => {
    const valueAsMoment = moment.utc(inputDate, moment.ISO_8601);

    if (valueAsMoment.isValid()) {
        return (valueAsMoment.format(format))
    }

    return null;
};

export const isCancellationProcess = (interactionProcess: string): boolean => {
    return interactionProcess === INTERACTION_PROCESS_MTC || interactionProcess === INTERACTION_PROCESS_MTC_FORCED ||
        interactionProcess === INTERACTION_PROCESS_SUPPORT_PORTAL_MTC || interactionProcess === INTERACTION_PROCESS_MTC_LV ||
        interactionProcess === INTERACTION_PROCESS_SUPPORT_PORTAL_FORCE_MTC
};

export const allowToRenderFutureMTAExplainedHeader = (interactionProcess: string): boolean => {
    return interactionProcess !== INTERACTION_PROCESS_NB &&
        interactionProcess !== INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY && interactionProcess !== INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE &&
        interactionProcess !== INTERACTION_PROCESS_RENEWAL && interactionProcess !== INTERACTION_PROCESS_CLAIM_IMPACT;
};

export function getNextInstalmentDate(instalmentsInfo: InstalmentsInfo): moment.Moment {
    const inFlightPayment: Instalment | undefined = instalmentsInfo.instalments.find((instalment: Instalment): boolean => instalment.status === INSTALMENT_STATUS_IN_FLIGHT);
    if (inFlightPayment) {
        return moment(new Date(inFlightPayment.dueDate));
    } else {
        const pendingPaymentInstalments: Instalment[] = instalmentsInfo.instalments.filter(
            (instalment: Instalment) => instalment.status === INSTALMENT_STATUS_PENDING_PAYMENT && !instalment.description.includes("Deposit"));
        const dueDates: Date[] = pendingPaymentInstalments.map((instalment: Instalment) => new Date(instalment.dueDate));
        return moment(new Date(Math.min.apply(null, dueDates)));
    }
}

export function getProductByPolicyNumber(policyNumber: string): ProductsEnum {
    if (policyNumber.startsWith("FLH")) {
        return ProductsEnum.HOME;
    } else if (policyNumber.startsWith("FLO")) {
        return ProductsEnum.WOLF_MOTOR;
    } else {
        return ProductsEnum.SUBSCRIPTION;
    }
}

export function calculateInstallmentPayment(instalments: Instalment[] | undefined): number {
    return instalments && instalments.length > 1 ? instalments[instalments.length - 1].amount : 0;
}

export function calculateWorkingDaysBetweenDates(dateFrom: Moment, dateTo: Moment): number {
    let numberOfWorkingDays = 0;

    while (dateFrom.isSameOrBefore(dateTo)) {
        if (dateFrom.day() !== 0 && dateFrom.day() !== 6) {
            numberOfWorkingDays++;
        }
        dateFrom.add(1, 'days');
    }
    return numberOfWorkingDays;
}

export function findLatestNotFutureMTAPolicyVersion(policies: Policy[], policyNumber: string) {
    const policiesFiltered =  policies.filter(p => p.policyNumber === policyNumber && moment().isAfter(p.interaction.headerInfo.startDate));

    if(policiesFiltered != null && policiesFiltered.length > 0) {

       return policiesFiltered.reduce((latestPolicy, currentPolicy) => {
            return latestPolicy === undefined || moment(currentPolicy.interaction.createdOn)
                .isAfter(moment(latestPolicy.interaction.createdOn))
                ? currentPolicy : latestPolicy;
        }, policiesFiltered[0]);
    }
    return undefined;
}

export function findPreviousPolicyVersionByAgreementVersion(policies: Policy[], policyNumber: string) {
    const allPolicies = policies.filter(p => p.policyNumber === policyNumber);
    allPolicies.sort((a, b) => {
        return b.interaction.agreementVersion - a.interaction.agreementVersion;
    });
    return allPolicies[1];
}

export function getIsOptionalExtraIncludedText(coverTier: string, optionalExtra: any): string {
    if (coverTier === undefined || coverTier === "" || coverTier === ALLIANZ) {
        return "Included as standard";
    }

    return optionalExtra ? "Included" : "Not included";
}

export function getGHCIncludeDescription(coverTier: string): string {
    if (coverTier === ALLIANZ_SILVER) {
        return "Guaranteed hire car if your car’s stolen or written off:";
    } else {
        return "Guaranteed hire car if your car’s stolen, written off or being repaired:";
    }
}

export function validPolicy(allPolicies: Policy[], policyNumber: string, productType: ProductsEnum) : Policy | undefined {
    const policy = currentValidPolicy(allPolicies, policyNumber, productType)

    return policy === undefined && allPolicies.length !== 0 ?
        futureValidPolicy(allPolicies, policyNumber, productType) : policy;
}

export function getCoverTierWording(coverTier?: string): string {
    switch (coverTier) {
        case ALLIANZ_ESSENTIALS:
            return "Allianz Essentials";
        case ALLIANZ_SILVER:
            return "Allianz Silver";
        default:
            return "Allianz";
    }
}

function currentValidPolicy(allPolicies: Policy[], policyNumber: string, productType: ProductsEnum) : Policy | undefined {
    return allPolicies
        .filter(policy => moment().isSameOrAfter(policy.interaction.headerInfo.startDate))
        .sort((policyA, policyB) => moment(policyB.interaction.headerInfo.startDate).diff(policyA.interaction.headerInfo.startDate))
        .find(policy => policy.policyNumber === policyNumber && policy.products.includes(productType));
}

function futureValidPolicy(allPolicies: Policy[], policyNumber: string, productType: ProductsEnum) : Policy | undefined {
    return [...allPolicies]
        .sort((policyA, policyB) => moment(policyA.interaction.headerInfo.startDate).diff(policyB.interaction.headerInfo.startDate))
        .find(policy => policy.policyNumber === policyNumber && policy.products.includes(productType));
}



// WEBPACK FOOTER //
// ./src/CommonFunctions.ts